import React, { useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { IconButtonWithTooltip, RecordContextProvider, useListContext, useRecordContext, useTranslate } from "react-admin"
import { Badge, Chip, CircularProgress, Collapse, Divider, List, ListItem, makeStyles, Paper, Tooltip, Typography } from "@material-ui/core"
import { Close, KeyboardArrowDown, KeyboardArrowUp, LocationOnOutlined, PeopleOutline, Schedule } from "@material-ui/icons"
import moment from "moment-timezone"

import { dedupe } from "@hisports/parsers"
import { toArray } from "@hisports/common/src/lodash"

import { useSeasonClickContext } from "../../contexts/SeasonClickContext"
import { useSchedulingContext } from "../../contexts/SchedulingContext"

import { Group, Stack } from "../../../Flex"
import { SurfaceField } from "../../../../resources/surfaces/SurfaceField"
import { useSlotsInUse } from "../../../../resources/arenaslots/useSlotsInUse"
import { isSlotInUse } from "../../../../resources/games/GameAvailabilityInfo"
import { getSurfaceName } from "../../../../resources/surfaces/util"

const useStyles = makeStyles(theme => {
  const borderStyles = `1px solid ${theme.palette.divider}`;

  return {
    listItemPaper: {
      width: '100%',
      padding: theme.spacing(0.5),
      borderTop: borderStyles,
      borderInline: borderStyles,
      '&:first-child': {
        borderTopLeftRadius: theme.spacing(1),
        borderTopRightRadius: theme.spacing(1),
      },
      '&:last-child': {
        borderBottom: borderStyles,
        borderBottomLeftRadius: theme.spacing(1),
        borderBottomRightRadius: theme.spacing(1),
      },
    },
    truncate: {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    },
    section: {
      marginTop: theme.spacing(1),
      padding: theme.spacing(1),
    },
    legend: {
      position: 'relative',
      border: borderStyles,
      borderRadius: theme.spacing(1),
      padding: theme.spacing(1),
    },
    badge: {
      marginInline: theme.spacing(1),
    },
    chipExtraSmall: {
      height: theme.spacing(2),
      fontSize: theme.typography.pxToRem(12),
      padding: theme.spacing(0.5),
      '& .MuiChip-label': {
        paddingInline: theme.spacing(0.5),
      },
    },
  }
})

const SurfaceFieldWithTooltip = (props) => {
  const record = useRecordContext(props);
  const surface = useSelector(state => state.admin.resources.surfaces.data[record?.[props.source]])
  const classes = useStyles();

  const surfaceName = getSurfaceName(surface) || '';

  return <Tooltip title={surfaceName} placement="top">
    <div className={classes.truncate}>
      <SurfaceField link={false} {...props} />
    </div>
  </Tooltip>
}

const SectionLegend = ({ label, children, ...props }) => {
  const classes = useStyles();

  return <div {...props}>
    {label && <Typography align="center" variant="body1" gutterBottom style={{ fontWeight: 500 }}>{label}</Typography>}
    <Stack className={classes.legend} justify="center" wrap="wrap">{children}</Stack>
  </div>
}

const SectionLegendItem = ({ color, text }) => {
  const classes = useStyles();
  return <Group gap={0.5}>
    <Badge className={classes.badge} variant="dot" color={color} />
    <Typography variant="body2" color="textSecondary">
      {text}
    </Typography>
  </Group>
}

const SectionHeader = ({ label, icon: Icon }) => {
  return <Group justify="center" style={{ marginBottom: 8 }}>
    <Icon fontSize="small" />
    <Typography variant="body2" style={{ fontWeight: 500 }}>
      {label}
    </Typography>
  </Group>
}

const ArenasAccordion = ({ arenaIds, selectedSlot, handleSelect, slotsInUse, slotsInUseLoading }) => {
  const { availability } = useSchedulingContext();
  const { selectedDate, opponent, isRefreshing } = useSeasonClickContext();
  const [openArena, setOpenArena] = useState(arenaIds[0] || null);
  const classes = useStyles();

  const toggleOpen = (arenaId) => setOpenArena(prev => (prev === arenaId ? null : arenaId));

  return <List disablePadding>
    {arenaIds.map((arenaId) => (
      <Stack key={arenaId} className={classes.listItemPaper} component={ListItem} gap={0} disableGutters>
        <Group justify="space-between" onClick={() => toggleOpen(arenaId)} style={{ cursor: 'pointer' }} >
          <LocationOnOutlined fontSize="small" />
          <RecordContextProvider value={{ arenaId }}>
            <SurfaceFieldWithTooltip source="arenaId" variant="body2" />
          </RecordContextProvider>
          {openArena === arenaId ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
        </Group>

        <Collapse in={openArena === arenaId}>
          <List>
            {availability?.slots
              .filter(slot => slot.date === selectedDate && slot.arenaId === arenaId)
              .map((slot) => {
                const startTime = moment.tz(slot.startTime, slot.timezone).format('HH:mm');
                const endTime = moment.tz(slot.endTime, slot.timezone).format('HH:mm z');
                const isInUse = isSlotInUse(slotsInUse, slot.id, slot);

                return <Group
                  key={slot.id}
                  component={ListItem}
                  className={classes.listItemPaper}
                  button
                  disableGutters
                  disabled={!opponent || isRefreshing || isInUse || slotsInUseLoading}
                  selected={selectedSlot?.id === slot.id}
                  onClick={() => handleSelect(slot)}
                >
                  <Badge className={classes.badge} variant="dot" color={slotsInUseLoading ? 'secondary' : (isInUse ? 'error' : 'primary')} />
                  <Typography variant="body2">
                    {startTime} - {endTime}
                  </Typography>
                </Group>;
              })}
          </List>
        </Collapse>
      </Stack>
    ))}
  </List>
}

const Slots = () => {
  const { selectedDate, selectedDateSlots, selectedTeam, slot, handleSelectSlot } = useSeasonClickContext();
  const translate = useTranslate();
  const classes = useStyles();

  const homeSlots = selectedDateSlots.filter(slot => slot.officeId === selectedTeam.officeId);

  const { data: slotsInUse, loading: slotsInUseLoading } = useSlotsInUse(homeSlots.map(slot => slot.id));

  const arenaIds = dedupe(homeSlots.map(slot => slot.arenaId) || []);

  return <div className={classes.section}>
    <SectionHeader
      label={moment(selectedDate).format('dddd, MMMM D, YYYY')}
      icon={Schedule}
    />
    {arenaIds.length === 0 ? (
      <Typography align="center" variant="body2" style={{ marginBlock: 24 }}>
        {translate('components.calendar.messages.no_slots')}
      </Typography>
    ) : (
      <ArenasAccordion key={JSON.stringify(arenaIds)} arenaIds={arenaIds} selectedSlot={slot} handleSelect={handleSelectSlot} slotsInUse={slotsInUse} slotsInUseLoading={slotsInUseLoading} />
    )}
  </div>
}

const Matchups = () => {
  const { selectedDateSlots, selectedTeam, opponent: selectedOpponent, handleSelectOpponent, isRefreshing } = useSeasonClickContext();
  const { scheduleTeams } = useSchedulingContext();
  const { data } = useListContext();
  const translate = useTranslate();
  const classes = useStyles();

  const selectedTeamId = selectedTeam?.id;

  const homeMatchups = useMemo(() => {
    const unscheduledDraftGames = toArray(data).filter(event => event.type === 'Draft Game' && !event.date);
    return unscheduledDraftGames.filter(event => event.homeTeamId === selectedTeamId);
  }, [data, selectedTeamId]);

  const opponents = useMemo(() => {
    const opponentMap = new Map();

    homeMatchups.forEach(event => {
      const opponentId = event.awayTeamId;
      const opponent = scheduleTeams.find(team => team.teamId === opponentId);
      if (!opponent) return;

      const homeSlotsAvailable = selectedDateSlots.some(slot => slot.officeId === opponent.team.officeId);

      if (!opponentMap.has(opponentId)) {
        opponentMap.set(opponentId, {
          opponentName: opponent.team.name,
          opponentId,
          count: 0,
          matchups: [],
          homeSlotsAvailable,
        });
      }

      const opponentData = opponentMap.get(opponentId);
      opponentData.count++;
      opponentData.matchups.push(event);
    });

    return Array.from(opponentMap.values()).sort((a, b) => a.opponentName.localeCompare(b.opponentName));
  }, [homeMatchups, scheduleTeams, selectedDateSlots]);

  return (
    <div className={classes.section}>
      <SectionHeader
        label={selectedTeam?.name}
        icon={PeopleOutline}
      />
      {opponents.length === 0 ? (
        <Typography align="center" variant="body2" style={{ marginBlock: 24 }}>
          {translate('components.calendar.messages.no_matchups')}
        </Typography>
      ) : (
        <List disablePadding>
          {opponents.map((opponent) => {
            const { opponentName, opponentId, count, homeSlotsAvailable } = opponent;

            return <Group
              key={opponentId}
              component={ListItem}
              className={classes.listItemPaper}
              button
              selected={selectedOpponent?.opponentId === opponentId}
              onClick={() => handleSelectOpponent(opponent)}
              disabled={isRefreshing}
            >
              <Badge className={classes.badge} variant="dot" color={homeSlotsAvailable ? 'primary' : 'error'} />
              <Tooltip title={opponentName} placement="top">
                <Typography className={classes.truncate} variant="body2">
                  {opponentName}
                </Typography>
              </Tooltip>
              <Tooltip title={translate('components.calendar.tooltips.matchup_count', count)} placement="top">
                <Chip className={classes.chipExtraSmall} label={`x${count}`} style={{ marginLeft: 'auto' }} />
              </Tooltip>
            </Group>
          })}
        </List>
      )}
    </div>
  );
};

const Instructions = () => {
  const translate = useTranslate();

  const steps = [
    translate("components.calendar.messages.season_click_steps.select_cell"),
    translate("components.calendar.messages.season_click_steps.select_opponent"),
    translate("components.calendar.messages.season_click_steps.select_slot"),
  ];

  return (
    <Paper variant="outlined" style={{ position: 'sticky', top: 16, padding: 16, margin: 8 }}>
      <Stack gap={2}>
        <div>
          <Typography align="center" variant="body1" gutterBottom style={{ fontWeight: 500 }}>
            {translate("components.calendar.labels.instructions")}
          </Typography>
          {steps.map((text, index) => (
            <Group key={index} align="flex-start" style={{ marginBottom: 8 }}> {/* eslint-disable-line react/no-array-index-key */}
              <Typography variant="body1" style={{ fontWeight: 500 }}>
                {index + 1}.
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {text}
              </Typography>
            </Group>
          ))}
        </div>
        <SectionLegend label={translate("components.calendar.labels.matchups_legend")}>
          <SectionLegendItem color="primary" text={translate('components.calendar.labels.has_home_slots')} />
          <SectionLegendItem color="error" text={translate('components.calendar.labels.no_home_slots')} />
        </SectionLegend>
        <SectionLegend label={translate("components.calendar.labels.slots_legend")}>
          <SectionLegendItem color="primary" text={translate('components.calendar.labels.slot_available')} />
          <SectionLegendItem color="error" text={translate('components.calendar.labels.slot_unavailable')} />
        </SectionLegend>
      </Stack>
    </Paper>
  );
}

const SeasonClickSidePanel = () => {
  const { selectedDate, selectedTeam, handleSelectCell, isRefreshing } = useSeasonClickContext();


  if (!selectedTeam || !selectedDate) return <Instructions />

  return <div style={{ position: 'sticky', top: 16 }}>
    <IconButtonWithTooltip label="components.calendar.tooltips.close" size="small" onClick={() => handleSelectCell(null, null)} style={{ position: 'absolute', top: 0, right: 8 }}>
      <Close fontSize="small" />
    </IconButtonWithTooltip>
    <Matchups />
    <Divider />
    <Slots />
    {isRefreshing && <Group justify="center" style={{ marginTop: 16 }}>
      <CircularProgress size={32} />
    </Group>}
  </div>
}

export default SeasonClickSidePanel;
